// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/** Pure note ↔ frequency maths, and cents. A4 = MIDI 69. */

import type { Accidental, NoteInfo } from './types';

const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

/** Frequency (Hz) of a MIDI note at the given A4 reference. */
export function midiToFrequency(midi: number, referenceA = 440): number {
  return referenceA * Math.pow(2, (midi - 69) / 12);
}

/** Exact (fractional) MIDI number of a frequency at the given A4 reference. */
export function frequencyToMidiExact(hz: number, referenceA = 440): number {
  return 69 + 12 * Math.log2(hz / referenceA);
}

/** Note name (no octave) for a MIDI number. */
export function noteName(midi: number, accidental: Accidental = 'sharp'): string {
  const names = accidental === 'flat' ? FLAT_NAMES : SHARP_NAMES;
  return names[((midi % 12) + 12) % 12];
}

/** Octave number (scientific pitch notation) for a MIDI number. */
export function octaveOf(midi: number): number {
  return Math.floor(midi / 12) - 1;
}

/** Full label, e.g. "E2", "A4", "Gb3". */
export function noteLabel(midi: number, accidental: Accidental = 'sharp'): string {
  return `${noteName(midi, accidental)}${octaveOf(midi)}`;
}

/**
 * Name a frequency: nearest note, its octave, and the signed cents deviation.
 * Returns null for a non-finite / non-positive frequency.
 */
export function describeFrequency(hz: number, referenceA = 440, accidental: Accidental = 'sharp'): NoteInfo | null {
  if (!isFinite(hz) || hz <= 0) return null;
  const exact = frequencyToMidiExact(hz, referenceA);
  const midi = Math.round(exact);
  const cents = (exact - midi) * 100;
  return {
    midi,
    name: noteName(midi, accidental),
    octave: octaveOf(midi),
    cents,
    targetHz: midiToFrequency(midi, referenceA),
  };
}

/** Signed cents between a frequency and a target frequency. */
export function centsBetween(hz: number, targetHz: number): number {
  return 1200 * Math.log2(hz / targetHz);
}
